import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  Timestamp,
  getDoc
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { RichNote, CreateRichNoteData, UpdateRichNoteData } from '../types/richNotes';

const COLLECTION_NAME = 'richNotes';

export const richNotesService = {
  // Get all notes for a user
  async getNotes(userId: string, includeArchived: boolean = false): Promise<RichNote[]> {
    try {
      const notesRef = collection(db, COLLECTION_NAME);
      let q = query(
        notesRef,
        where('userId', '==', userId),
        orderBy('updatedAt', 'desc')
      );

      if (!includeArchived) {
        q = query(
          notesRef,
          where('userId', '==', userId),
          where('isArchived', '==', false),
          orderBy('updatedAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as RichNote[];
    } catch (error) {
      console.error('Error fetching notes:', error);
      throw error;
    }
  },

  // Get a single note by ID
  async getNote(noteId: string): Promise<RichNote | null> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      const noteDoc = await getDoc(noteRef);
      
      if (!noteDoc.exists()) {
        return null;
      }

      return {
        id: noteDoc.id,
        ...noteDoc.data(),
        createdAt: noteDoc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: noteDoc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      } as RichNote;
    } catch (error) {
      console.error('Error fetching note:', error);
      throw error;
    }
  },

  // Create a new note
  async createNote(userId: string, noteData: CreateRichNoteData): Promise<RichNote> {
    try {
      const now = Timestamp.now();
      const newNote = {
        ...noteData,
        userId,
        content: noteData.content || '',
        plainText: noteData.plainText || '',
        category: noteData.category || 'Általános',
        tags: noteData.tags || [],
        color: noteData.color || 'blue',
        isPinned: noteData.isPinned || false,
        isFavorite: noteData.isFavorite || false,
        isArchived: false,
        createdAt: now,
        updatedAt: now,
        attachments: [],
      };

      const docRef = await addDoc(collection(db, COLLECTION_NAME), newNote);
      
      return {
        id: docRef.id,
        ...newNote,
        createdAt: now.toDate().toISOString(),
        updatedAt: now.toDate().toISOString(),
      } as RichNote;
    } catch (error) {
      console.error('Error creating note:', error);
      throw error;
    }
  },

  // Update a note
  async updateNote(noteId: string, updates: UpdateRichNoteData): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      await updateDoc(noteRef, {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error updating note:', error);
      throw error;
    }
  },

  // Delete a note
  async deleteNote(noteId: string): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      await deleteDoc(noteRef);
    } catch (error) {
      console.error('Error deleting note:', error);
      throw error;
    }
  },

  // Archive a note
  async archiveNote(noteId: string): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      await updateDoc(noteRef, {
        isArchived: true,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error archiving note:', error);
      throw error;
    }
  },

  // Unarchive a note
  async unarchiveNote(noteId: string): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      await updateDoc(noteRef, {
        isArchived: false,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error unarchiving note:', error);
      throw error;
    }
  },

  // Toggle pin status
  async togglePin(noteId: string, isPinned: boolean): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      await updateDoc(noteRef, {
        isPinned,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error toggling pin:', error);
      throw error;
    }
  },

  // Toggle favorite status
  async toggleFavorite(noteId: string, isFavorite: boolean): Promise<void> {
    try {
      const noteRef = doc(db, COLLECTION_NAME, noteId);
      await updateDoc(noteRef, {
        isFavorite,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
      throw error;
    }
  },

  // Get notes by category
  async getNotesByCategory(userId: string, category: string): Promise<RichNote[]> {
    try {
      const notesRef = collection(db, COLLECTION_NAME);
      const q = query(
        notesRef,
        where('userId', '==', userId),
        where('category', '==', category),
        where('isArchived', '==', false),
        orderBy('updatedAt', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        updatedAt: doc.data().updatedAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as RichNote[];
    } catch (error) {
      console.error('Error fetching notes by category:', error);
      throw error;
    }
  },
};
